/* @flow */

import { batch } from 'react-redux';
import {
  getBraindateConversation,
  normalizeBraindate,
  normalizeBraindates,
} from '@e180/domain/lib/braindate/util';
import {
  getConversationId,
  getConversationUnreadNotificationsCount,
} from '@e180/domain/lib/conversation/util';
import { getEventId } from '@e180/domain/lib/event/util';

import * as types from './braindateActionTypes';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import {
  braindateEndpoint,
  braindatesEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';

import type { Braindate } from '@e180/domain/lib/braindate/type';

// Braindates

export function simulateBraindatesFetch() {
  return {
    type: types.SIMULATE_BRAINDATES_FETCH,
  };
}

export function fetchBraindatesOptimally() {
  return fetchBraindates(true);
}

export function fetchBraindates(optimized: boolean = false) {
  return (dispatch: any, { get }: Object, getState: any) => {
    const eventId = getEventId(getEventSafely(getState()));

    dispatch(fetchBraindatesRequest());

    const params = {};

    if (optimized) {
      params.for_braindate_screen = true;
    }

    return get(braindatesEndpoint(eventId), params)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeBraindates(json);
        const { entities, result } = normalizedData;

        batch(() => {
          dispatch(patchEntities(entities));
          // $FlowIssue
          dispatch(fetchBraindatesSuccess(result));
        });
      })
      .catch((e) => {
        dispatch(fetchBraindatesFailure(e));

        throw e;
      });
  };
}

export function fetchBraindatesRequest() {
  return {
    type: types.FETCH_BRAINDATES_REQUEST,
  };
}

export function fetchBraindatesSuccess(result: Array<Braindate>) {
  return {
    type: types.FETCH_BRAINDATES_SUCCESS,
    result,
  };
}

export function fetchBraindatesFailure(exception: Error) {
  return {
    type: types.FETCH_BRAINDATES_FAILURE,
    exception,
  };
}

// Single braindate

export function simulateBraindateFetch() {
  return {
    type: types.SIMULATE_BRAINDATE_FETCH,
  };
}

export function fetchBraindate(id: number) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchBraindateRequest());

    return get(braindateEndpoint(id))
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeBraindate(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchBraindateSuccess(result));

        return json;
      })
      .catch((exception) => {
        dispatch(fetchBraindateFailure(exception));

        throw exception;
      });
  };
}

export function fetchBraindateRequest() {
  return {
    type: types.FETCH_BRAINDATE_REQUEST,
  };
}

export function fetchBraindateSuccess(result: number) {
  return {
    type: types.FETCH_BRAINDATE_SUCCESS,
    result,
  };
}

export function fetchBraindateFailure(exception: Error) {
  return {
    type: types.FETCH_BRAINDATE_FAILURE,
    exception,
  };
}

// Create braindate

export function createBraindate(braindate: Braindate) {
  return (dispatch: any) => {
    const normalizedData = normalizeBraindate(braindate);
    const { entities, result } = normalizedData;

    dispatch(patchEntities(entities));
    // $FlowIssue
    dispatch(_createBraindate(result));
  };
}

export function _createBraindate(braindateId: number) {
  return {
    type: types.CREATE_BRAINDATE,
    braindateId,
  };
}

// Delete braindate

export function deleteBraindateLocally(id: number) {
  return {
    type: types.DELETE_BRAINDATE_LOCALLY,
    id,
  };
}

// Update unread count

export function updateUnreadCount(braindate: Braindate) {
  return (dispatch: any) => {
    const conversation = getBraindateConversation(braindate);

    dispatch(
      _updateUnreadCount(
        getConversationId(conversation),
        getConversationUnreadNotificationsCount(conversation),
      ),
    );
  };
}

export function _updateUnreadCount(conversationId: number, count: ?number) {
  return {
    type: types.UPDATE_CONVERSATION_UNREAD_COUNT,
    conversationId,
    count,
  };
}
